import React from 'react';
import { motion } from 'framer-motion';
import { Download, LogIn } from 'lucide-react';

// Base URL: en el cliente usar origin actual (fix Amplify); en build/SSR usar PUBLIC_URL
const getBaseUrl = () =>
  typeof window !== 'undefined' ? window.location.origin : (process.env.PUBLIC_URL || '');
const heroPathJpeg = '/img/hero/atleta-coach-optimized.jpg';
const heroPathWebp = '/img/hero/atleta-coach.webp';

interface HeroSectionProps {
  onDownloadClick: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ onDownloadClick }) => {
  const baseUrl = getBaseUrl();
  const heroImageJpeg = baseUrl + heroPathJpeg;
  const heroImageWebp = baseUrl + heroPathWebp;

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Optimized Image Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/80 via-blue-800/60 to-blue-900/90 z-10" />
       <picture>
           <img
             src={heroImageJpeg}
             alt="+Trainer - Deportista y Coach"
             className="w-full h-full object-cover"
           />
         </picture>
      </div>

      {/* Content */}
      <div className="relative z-20 container-custom text-center text-white px-4 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto"
        >
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="pt-4 md:pt-6"
          >
            <div className="inline-flex items-center justify-center w-48 h-48 md:w-64 md:h-64 lg:w-80 lg:h-80 p-2">
              <img 
                src="/logo_trainer_blanco.png" 
                alt="+Trainer Logo" 
                className="w-full h-full object-contain"
              />
            </div>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-3xl md:text-5xl lg:text-6xl font-display mb-6 leading-tight"
          >
            <span className="block font-bold text-mas10-accent mb-2">Plataforma profesional de entrenamiento</span>
          </motion.h1>

          {/* Sub-headline */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-xl md:text-2xl text-gray-200 mb-12 max-w-4xl mx-auto leading-relaxed"
          >
            Planificación, monitoreo de carga y prevención de lesiones para profesionales del entrenamiento.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mb-12"
          >
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              {/* Unified Download Button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onDownloadClick}
                className="bg-mas10-primary text-white px-8 py-4 rounded-full font-bold text-lg group flex items-center space-x-3 hover:shadow-lg transition-all duration-300"
              >
                <Download className="w-6 h-6 group-hover:animate-bounce" />
                <span>App</span>
              </motion.button>

              {/* Iniciar Sesión (Entrenador) Button */}
              <motion.a
                href="https://plans.mastrainerapp.com/login"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-mas10-primary text-white px-8 py-4 rounded-full font-bold text-lg group flex items-center space-x-3 hover:shadow-lg transition-all duration-300"
              >
                <LogIn className="w-6 h-6 group-hover:animate-pulse" />
                <span>Iniciar Sesión (Entrenador)</span>
              </motion.a>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1 h-3 bg-white/60 rounded-full mt-2"
          ></motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
